import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Table } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMatch, useSearchParams } from 'react-router';
import { Box, useTheme } from '@/shared/ui';

import { captureEvent } from '@/services/analytics';

import type { CatalogItem } from '@/entities/catalog-item';
import { useFactsheetOverlayContainer } from '@/features/factsheet';
import { extractExistingParams } from '../../lib/extractExistingParams';
import ActiveFilterChips from './ActiveFilterChips';
import CatalogTableGrid from './CatalogTableGrid/CatalogTableGrid';
import CatalogToolbar from './CatalogToolbar';
import {
  COLLAPSED_ROW_HEIGHT,
  FILTER_DRAWER_TRANSITION_DURATION,
  FILTER_DRAWER_WIDTH
} from './constants';
import DisplaySettingsPanel from './DisplaySettings/DisplaySettingsPanel';
import TableFilters from './TableFilters/TableFilters';

const CatalogTable = ({
  filterOpen,
  setFilterOpen,
  table,
  isForwardCurves,
  expandedData,
  setExpandedData,
  forwardPeriodsInline,
  setForwardPeriodsInline,
  periodGroups,
  setShowDiscontinued,
  showDiscontinued,
  displaySettingsOpen,
  setDisplaySettingsOpen,
  renderFactsheetButton
}: {
  filterOpen: boolean;
  setFilterOpen: (filterOpen: boolean) => void;
  table: Table<CatalogItem>;
  isForwardCurves: boolean;
  expandedData: boolean;
  setExpandedData: (newValue: boolean) => void;
  forwardPeriodsInline: boolean;
  setForwardPeriodsInline: Dispatch<SetStateAction<boolean>>;
  periodGroups: Map<string, CatalogItem[]>;
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
  displaySettingsOpen: boolean;
  setDisplaySettingsOpen: Dispatch<SetStateAction<boolean>>;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}) => {
  const { transitions } = useTheme();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const isInDashboard = Boolean(useMatch('/dashboard/*'));
  const [searchParams] = useSearchParams();
  const rows = table.getRowModel().rows;
  const { setContainer } = useFactsheetOverlayContainer();

  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());
  const [fullyExpandedCodes, setFullyExpandedCodes] = useState<Set<string>>(new Set());

  const onToggleExpand = useCallback((code: string) => {
    setExpandedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    setFullyExpandedCodes(prev => {
      if (!prev.has(code)) return prev;
      const next = new Set(prev);
      next.delete(code);
      return next;
    });
  }, []);

  const onShowAll = useCallback((code: string) => {
    setFullyExpandedCodes(prev => new Set(prev).add(code));
  }, []);

  // Reset expansion when the feature turns off or the tab changes.
  useEffect(() => {
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect
    setExpandedCodes(new Set());
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect
    setFullyExpandedCodes(new Set());
  }, [forwardPeriodsInline, isForwardCurves]);

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- TanStack Virtual powers catalog row windowing.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => COLLAPSED_ROW_HEIGHT,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5,
    measureElement: element => element?.getBoundingClientRect().height ?? COLLAPSED_ROW_HEIGHT,
    getItemKey: index => {
      const item = rows[index]?.original;
      return item ? `${item.Code}|${item.PeriodType}|${item.Period}|${item.TimeRef}` : index;
    }
  });

  useEffect(() => {
    captureEvent('catalogue_filter', extractExistingParams(searchParams));
  }, [searchParams]);

  return (
    <>
      <CatalogToolbar
        displaySettingsOpen={displaySettingsOpen}
        filterOpen={filterOpen}
        isForwardCurves={isForwardCurves}
        setDisplaySettingsOpen={setDisplaySettingsOpen}
        setFilterOpen={setFilterOpen}
        table={table}
      />
      {displaySettingsOpen && (
        <DisplaySettingsPanel
          expandedData={expandedData}
          forwardPeriodsInline={forwardPeriodsInline}
          isForwardCurves={isForwardCurves}
          setExpandedData={setExpandedData}
          setForwardPeriodsInline={setForwardPeriodsInline}
          setShowDiscontinued={setShowDiscontinued}
          showDiscontinued={showDiscontinued}
          table={table}
        />
      )}
      <ActiveFilterChips />
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <TableFilters
          filterOpen={filterOpen}
          height='100%'
          setFilterOpen={setFilterOpen}
          table={table}
        />
        <Box
          ref={setContainer}
          className='catalog-table-container'
          sx={{
            position: 'relative',
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            ml: filterOpen ? 0 : `${-FILTER_DRAWER_WIDTH}px`,
            transition: transitions.create('margin-left', {
              easing: transitions.easing.easeInOut,
              duration: FILTER_DRAWER_TRANSITION_DURATION
            })
          }}
        >
          {rows.length ? (
            <CatalogTableGrid
              expandedCodes={expandedCodes}
              expandedData={expandedData}
              forwardPeriodsInline={forwardPeriodsInline}
              fullyExpandedCodes={fullyExpandedCodes}
              isForwardCurves={isForwardCurves}
              isInDashboard={isInDashboard}
              onShowAll={onShowAll}
              onToggleExpand={onToggleExpand}
              periodGroups={periodGroups}
              renderFactsheetButton={renderFactsheetButton}
              rowVirtualizer={rowVirtualizer}
              rows={rows}
              table={table}
              tableContainerRef={tableContainerRef}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              Nothing to display here. Try changing your filters.
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CatalogTable;
